import { isPlatformBrowser } from '@angular/common';
import { DestroyRef, effect, inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import {
  Breakpoint,
  BREAKPOINTS,
  Color,
  getBreakpointForWidth,
  GsStructBorderRadius,
  GsStructBorderStyle,
  GsStructBorderWidth,
  GsStructButtonSize,
  GsStructFont,
  GsStructPageItems,
  GsStructTextAlignment,
  IInputWidth,
  ISides,
  isResponsiveAttribute,
  MaybeResponsive,
  RESPONSIVE_STYLE_PROPERTIES,
  ResponsiveAttribute,
  ResponsiveStyleProperty,
  TAILWIND_BREAKPOINTS,
  TextTransformType
} from '../../models';
import { isHexColor, isTailwindColor } from '../color.service';

const DEFAULT_H_ALIGNMENT_CLASSES: Record<number, string> = {
  0: 'justify-start',
  1: 'justify-center',
  2: 'justify-end'
};
const DEFAULT_V_ALIGNMENT_CLASSES: Record<number, string> = {
  0: 'items-start',
  1: 'items-center',
  2: 'items-end'
};
/** `align-items` keywords mirroring {@link DEFAULT_V_ALIGNMENT_CLASSES}, for the CSS-resolved path. */
const DEFAULT_V_ALIGNMENT_VALUES: Record<number | string, AlignItemsValue> = {
  0: 'flex-start',
  1: 'center',
  2: 'flex-end'
};
const DEFAULT_JUSTIFY_ITEMS_CLASSES: Record<number, string> = {
  0: 'justify-items-start',
  1: 'justify-items-center',
  2: 'justify-items-end'
};
const DEFAULT_WIDTH_CLASSES: Record<string, string> = {
  valueXS: 'xs',
  valueSM: 'sm',
  valueMD: 'md',
  valueLG: 'lg',
  valueXL: 'xl',
  valueXXL: 'xxl'
};
const BORDERSTYLES: Record<number, string> = {
  0: 'none',
  1: 'solid',
  2: 'dotted',
  3: 'dashed',
  4: 'double'
};
const BUTTON_SIZE_CLASSES: Record<GsStructButtonSize, string> = {
  0: ' px-4 py-3 text-sm ', // Small: padding 12px 16px, font-size 14px
  1: ' px-6 py-4 text-base ', // Medium: padding 16px 24px, font-size 16px
  2: ' px-8 py-5 text-lg ' // Large: padding 20px 32px, font-size 18px
};
const BORDERRADIUS: Record<number, string> = {
  0: 'none',
  1: 'sm',
  3: 'md',
  4: 'lg',
  5: 'xl',
  6: '2xl',
  7: '3xl',
  8: 'full'
};
const DEFAULT_TEXT_ALIGNMENT_CLASSES: Record<GsStructTextAlignment, string> = {
  0: 'text-left',
  1: 'text-center',
  2: 'text-right',
  3: 'text-justify'
};
const BORDERWIDTH: Record<number, string> = {
  0: '0',
  2: '2',
  3: '4',
  4: '8',
  5: '10',
  6: '12',
  7: '14',
  8: '16',
  9: '18',
  10: '20'
};

/**
 * Maps font weight names to their corresponding CSS class names
 */
const FONTWEIGHTCLASSES: Record<string, string> = {
  100: 'font-thin',
  200: 'font-extralight',
  300: 'font-light',
  400: 'font-normal',
  500: 'font-medium',
  600: 'font-semibold',
  700: 'font-bold',
  800: 'font-extrabold',
  900: 'font-black'
};

/**
 * Maps text transform types to their corresponding Tailwind CSS class names
 */
const TEXT_TRANSFORM_CLASSES: Record<TextTransformType, string> = {
  none: 'normal-case',
  uppercase: 'uppercase',
  lowercase: 'lowercase',
  capitalize: 'capitalize'
};

/** Side order of the `padding` shorthand. */
const PADDING_SIDES = ['top', 'right', 'bottom', 'left'] as const satisfies readonly (keyof ISides<number>)[];

/** Side order of `padding-inline`, which takes the inline start and end edge — left and right in LTR. */
const INLINE_SIDES = ['left', 'right'] as const satisfies readonly (keyof ISides<number>)[];

/** Layout modes `[gsResponsive]` can switch between per breakpoint. */
export type DisplayValue = 'block' | 'flex' | 'grid' | 'inline-block' | 'contents' | 'none';

/** Cross-axis alignments `[gsResponsive]` can switch between per breakpoint. */
export type AlignItemsValue = 'normal' | 'stretch' | 'flex-start' | 'center' | 'flex-end' | 'baseline';

/** Responsive values to hand to CSS, keyed by the value name used in `[gsResponsive]`. */
export interface ResponsiveStyleInput {
  /** Font size, resolved per breakpoint. */
  'font-size'?: MaybeResponsive<number>;
  /** Padding for all four sides, emitted as the `padding` shorthand. Sides cascade individually. */
  padding?: MaybeResponsive<ISides<number>>;
  /** Horizontal padding only, emitted as `padding-inline` from the `left`/`right` sides of an `ISides`. */
  'padding-inline'?: MaybeResponsive<ISides<number>>;
  /** Maximum width, resolved per breakpoint. `none` lifts the limit at that breakpoint. */
  'max-width'?: MaybeResponsive<number | 'none'>;
  /** Minimum height, resolved per breakpoint. */
  'min-height'?: MaybeResponsive<number>;
  /**
   * Height, resolved per breakpoint. Breakpoints without a value fall back to `auto`, so an
   * `aspect-ratio` on the same element takes over there. `auto` can also be set explicitly, to give
   * the height back at a breakpoint above one that sets it.
   */
  height?: MaybeResponsive<number | 'auto'>;
  /** Maximum height, resolved per breakpoint. `none` lifts the limit at that breakpoint. */
  'max-height'?: MaybeResponsive<number | 'none'>;
  /** Layout mode of the element, resolved per breakpoint. */
  display?: MaybeResponsive<DisplayValue>;
  /** Cross-axis alignment of the element's children, resolved per breakpoint. */
  'align-items'?: MaybeResponsive<AlignItemsValue>;
  /**
   * Whether the element is shown at a breakpoint. Resolves to `display: contents`/`none`, so the host
   * has to be a wrapper without layout of its own — its children take its place in the parent.
   */
  visible?: MaybeResponsive<boolean>;
}

/** The custom properties an element carries, plus the classes that consume them. */
export interface ResponsiveStyle {
  /** Utility classes to add, e.g. `['gs-r-font-size']`. Empty when nothing is configured. */
  classes: string[];
  /** Custom properties to set, e.g. `{ '--gs-r-font-size-default': '14px' }`. */
  variables: Record<string, string>;
}

@Injectable({
  providedIn: 'root'
})
export class StyleService {
  // Services
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);

  // Properties
  /** Signal holding the current device breakpoint key based on window width */
  public currentDeviceSize = signal<Breakpoint>('default');
  /** Signal holding the measured viewport width in px; `0` while it is unknown (server-side rendering) */
  public readonly viewportWidth = signal<number>(0);
  /** Signal holding the resize observer entry */
  private readonly resizeEntry = signal<ResizeObserverEntry | null>(null);
  /** Breakpoint forced via {@link setDeviceSize}; while set, window resizes do not change the device size */
  private readonly deviceSizeOverride = signal<Breakpoint | null>(null);

  constructor() {
    // Only run in browser environment
    if (isPlatformBrowser(this.platformId)) {
      this.setupResizeObserver();
      // Update device size when resize entry changes
      effect(() => {
        const body = this.resizeEntry();
        if (body) {
          this.updateDeviceSize(body.contentRect.width);
        }
      });
      // Initial update
      this.updateDeviceSize(window.innerWidth);
    }
  }

  /**
   * Sets up ResizeObserver on document.body to track viewport changes
   */
  private setupResizeObserver(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const obs = new ResizeObserver((entries) => {
      this.resizeEntry.set(entries[0]);
    });
    obs.observe(document.body);
    this.destroyRef.onDestroy(() => obs.disconnect());
  }

  /**
   * Manually sets the current device size breakpoint.
   * Useful for preview modes (e.g., iframe previews) where the actual viewport size differs.
   * The breakpoint is kept until it is reset with `null`, even when the window is resized.
   * @param breakpoint - The breakpoint to set, or null to resume automatic detection
   */
  public setDeviceSize(breakpoint: Breakpoint | null): void {
    this.deviceSizeOverride.set(breakpoint);
    if (breakpoint) {
      this.currentDeviceSize.set(breakpoint);
    } else if (isPlatformBrowser(this.platformId)) {
      // Resume automatic detection based on current window width
      this.updateDeviceSize(window.innerWidth);
    }
  }

  /**
   * Determines the current device size based on window width and Tailwind breakpoints
   * Updates the currentDeviceSize signal with the matching breakpoint key
   */
  private updateDeviceSize(width: number): void {
    if (width <= 0) {
      return;
    }
    this.viewportWidth.set(width);
    // A forced breakpoint (preview modes, gallery slide frame) wins over the measured window width
    if (this.deviceSizeOverride()) return;
    this.currentDeviceSize.set(getBreakpointForWidth(width));
  }

  /**
   * Returns the CSS class for horizontal alignment based on the given alignment value.
   * @param hAlignment - The horizontal alignment value (e.g. 'left', 'center', 'right' or numeric enum key).
   * @param options - Optional mapping of alignment values to CSS class names. Defaults to `DEFAULT_H_ALIGNMENT_CLASSES`.
   * @returns The corresponding CSS class name, or an empty string if no valid alignment is provided.
   */
  public getHorizontalAlignmentClass(
    hAlignment?: number | string,
    options: Record<number | string, string> = DEFAULT_H_ALIGNMENT_CLASSES
  ): string {
    if (hAlignment == undefined) return '';
    const cssClass = options[hAlignment];
    return cssClass ? ` ${cssClass} ` : '';
  }

  /**
   * Returns the CSS class for vertical alignment based on the given alignment value.
   * @param vAlignment - The vertical alignment value (e.g. 'top', 'middle', 'bottom' or numeric enum key).
   * @param options - Optional mapping of alignment values to CSS class names. Defaults to `DEFAULT_V_ALIGNMENT_CLASSES`.
   * @returns The corresponding CSS class name, or an empty string if the value is invalid or not found.
   */
  public getVerticalAlignmentClass(
    vAlignment?: number | string,
    options: Record<number | string, string> = DEFAULT_V_ALIGNMENT_CLASSES
  ): string {
    if (vAlignment == undefined) return '';
    const cssClass = options[vAlignment];
    return cssClass ? ` ${cssClass} ` : '';
  }

  /**
   * Returns the `align-items` keyword for the given vertical alignment, for the CSS-resolved path.
   * @param vAlignment - The vertical alignment value (numeric enum key or string).
   * @returns The matching `align-items` keyword, or `normal` when no valid alignment is provided.
   */
  public getAlignItemsValue(vAlignment?: number | string): AlignItemsValue {
    if (vAlignment == undefined) return 'normal';
    return DEFAULT_V_ALIGNMENT_VALUES[vAlignment] ?? 'normal';
  }

  /**
   * Projects a responsive value onto another one, resolving the mobile-first cascade at every
   * breakpoint so the result carries an explicit value everywhere.
   * @param value - The configured value, responsive or fixed.
   * @param map - Maps the value that applies at a breakpoint onto the projected one.
   * @returns The projected value per breakpoint.
   */
  public mapResponsiveValue<T, R>(
    value: MaybeResponsive<T> | undefined,
    map: (value: T | undefined, breakpoint: Breakpoint) => R
  ): ResponsiveAttribute<R> {
    const projected = { default: map(this.getResponsiveValueAt<T>(value, 'default'), 'default') } as ResponsiveAttribute<R>;
    for (const breakpoint of BREAKPOINTS) {
      projected[breakpoint] = map(this.getResponsiveValueAt<T>(value, breakpoint), breakpoint);
    }
    return projected;
  }

  /**
   * Fills the breakpoints a responsive value does not cover with a fallback, keeping the
   * per-breakpoint structure so CSS can still resolve it.
   *
   * On the raw configuration a plain `??` reaches for the fallback only when nothing at all is
   * configured, so a value starting at `lg` would lose it at the breakpoints below.
   * @param value - The configured value, responsive or fixed.
   * @param fallback - The value to use at breakpoints the configuration does not reach.
   * @returns The value per breakpoint, with the fallback filled in where nothing applies.
   */
  public withResponsiveFallback<T>(value: MaybeResponsive<T> | undefined, fallback: T): ResponsiveAttribute<T> {
    return this.mapResponsiveValue<T, T>(value, (resolved) => resolved ?? fallback);
  }

  /**
   * Resolves a responsive value at one breakpoint through the mobile-first cascade.
   * @param value - The configured value, responsive or fixed.
   * @param breakpoint - The breakpoint to resolve at.
   * @returns The value applying at that breakpoint, or undefined when none does.
   */
  public getResponsiveValueAt<T>(value: MaybeResponsive<T> | undefined, breakpoint: Breakpoint): T | undefined {
    return this.resolveResponsiveValueAt<T>(value, breakpoint);
  }

  /**
   * Returns the grid `justify-items-*` class for horizontally aligning a component inside a block.
   * Single-column grid blocks (used when a component fills the block height) ignore `justify-content`,
   * so the horizontal alignment must be applied via `justify-items` instead.
   * @param hAlignment - The horizontal alignment value (numeric enum key or string).
   * @returns The corresponding `justify-items-*` class, or an empty string if no valid alignment is provided.
   */
  public getJustifyItemsClass(hAlignment?: number | string): string {
    return this.getHorizontalAlignmentClass(hAlignment, DEFAULT_JUSTIFY_ITEMS_CLASSES);
  }

  /**
   * Returns the CSS class for text alignment based on the given alignment value.
   * @param textAlignment - The text alignment enum value (left, center, right, justify).
   * @param options - Optional mapping of alignment values to CSS class names. Defaults to `DEFAULT_TEXT_ALIGNMENT_CLASSES`.
   * @returns The corresponding CSS class name, or an empty string if no alignment is provided.
   */
  public getTextAlignmentClass(
    textAlignment?: GsStructTextAlignment | number,
    options: Record<GsStructTextAlignment, string> = DEFAULT_TEXT_ALIGNMENT_CLASSES
  ): string {
    if (textAlignment === undefined || textAlignment === null) return '';

    const cssClass = options[textAlignment as GsStructTextAlignment];
    return cssClass ? ` ${cssClass} ` : '';
  }

  /**
   * Generates the current padding value in pixels based on the provided configuration.
   * @param valueArgs - Configuration object containing padding values (also responsive)
   * @param side -Specific side to get padding for ('left', 'top', 'right', 'bottom').
   * @returns The padding value in pixels for the specified side.
   * If a responsive configuration is provided, it calculates the padding based on the current device size.
   */
  public getPadding(valueArgs: ResponsiveAttribute<ISides<number>> | ISides<number> | undefined, side: keyof ISides<number>): number {
    if (!valueArgs || !side) return 0;
    if (isResponsiveAttribute<ISides<number>>(valueArgs)) {
      // Work with responsive attributed ISides
      const responsiveValue = this.getCurrentResponsiveValue<ISides<number>>(valueArgs);
      if (responsiveValue) return responsiveValue[side] ?? 0;
      else return 0;
    } else {
      // Work with ISides
      return valueArgs[side] ?? 0;
    }
  }

  /**
   * Generates CSS class strings for padding based on provided configuration
   * @param valueArgs - Configuration object containing padding values (also responsive)
   * @returns A space-separated string of CSS classes for padding
   * @deprecated Please try to use getPadding for inline styles instead of classes, as it provides more accurate results and better performance. This method will be removed in future versions.
   */
  public getPaddingClasses(valueArgs?: ResponsiveAttribute<ISides> | ISides): string {
    let paddingClasses = '';
    if (!valueArgs) return '';
    if (isResponsiveAttribute<ISides>(valueArgs)) {
      // Work with responsive attributed ISides
      paddingClasses += this.getResponsiveClasses<ISides>(valueArgs as ResponsiveAttribute<ISides>, (val) => this.getPaddingClasses(val));
    } else {
      // Work with ISides
      for (const [key, value] of Object.entries(valueArgs as ISides)) {
        if (value >= 0) {
          paddingClasses += ` p${key.substring(0, 1)}-${value} `;
        }
      }
    }
    return paddingClasses;
  }

  /**
   * Generates responsive CSS class strings
   * @param valueArgs - Configuration object containing responsive values
   * @param getClassesMethod - Method to generate classes for a single value
   * @returns A space-separated string of CSS classes for the responsive property
   */
  private getResponsiveClasses<T = number | string>(valueArgs: ResponsiveAttribute<T>, getClassesMethod: (value: T) => string): string {
    let resultingClasses = '';
    // Iterate over each device key in the responsive attribute
    for (const [deviceKey, value] of Object.entries(valueArgs)) {
      // Checks if the value has a value (number > 0 or string with length > 0)
      let hasValue = false;
      if (value && typeof value === 'object' && value !== null) {
        hasValue = Object.values(value).some((v) => {
          if (typeof v === 'number') return v >= 0;
          else if (typeof v === 'string') return v.length > 0;
          return false;
        });
      } else if (typeof value === 'number') {
        hasValue = value >= 0;
      } else if (typeof value === 'string') {
        hasValue = value.length > 0;
      }
      // Only add classes if a value is set
      if (hasValue) {
        if (deviceKey === 'default') resultingClasses += getClassesMethod(value);
        else {
          const classes = getClassesMethod(value).trim().split(' ');
          // Add responsive prefix to each class
          resultingClasses +=
            classes
              .filter((cls) => cls.trim().length > 0)
              .map((cls) => `${deviceKey}:${cls}`)
              .join(' ') + ' ';
        }
      }
    }
    return resultingClasses;
  }

  /**
   * Generates CSS class strings for font styling based on provided configuration
   * @param valueArgs - Optional configuration object containing font properties
   * @returns A space-separated string of CSS classes for font styling
   */
  public getFontClasses(valueArgs?: GsStructFont): string {
    if (!valueArgs) return '';
    const { family, weight, style, textTransform } = valueArgs;
    let fontClasses = ' ';

    if (family) {
      fontClasses += `font-${family.toLowerCase()} `;
    }

    if (weight && weight !== 400) {
      fontClasses += `${FONTWEIGHTCLASSES[weight]} `;
    }

    if (style && style !== 'normal') {
      fontClasses += `${style} `;
    }

    if (textTransform) {
      fontClasses += `${TEXT_TRANSFORM_CLASSES[textTransform]} `;
    }
    return fontClasses;
  }

  /** Returns the margin classes */
  public getMarginClasses(valueArgs?: ISides): string {
    let marginClasses = '';
    if (!valueArgs) return '';
    for (const [key, value] of Object.entries(valueArgs)) {
      if (value >= 0) {
        marginClasses += ` m${key.substring(0, 1)}-${value}`;
      }
    }
    return marginClasses;
  }

  /**
   * Generates CSS classes for borders based on the provided parameters
   * @param color Border color
   * @param width Border width
   * @param style Border style
   * @param radius Border radius
   * @returns String with Tailwind CSS classes for borders or empty string if all values are undefined or null
   */
  public getBorderClasses(color?: Color, width?: GsStructBorderWidth, style?: GsStructBorderStyle, radius?: GsStructBorderRadius): string {
    if (this.areAllBorderPropsUndefined(color, width, style, radius)) {
      return '';
    }

    // If border style is explicitly "none", don't apply any border classes except radius
    if (style === GsStructBorderStyle.none) {
      const borderRadius = this.getBorderRadiusClass(radius);
      return borderRadius ? ` ${borderRadius} ` : '';
    }

    const borderColor = this.getBorderColorClass(color);
    const borderWidth = this.getBorderWidthClass(width);
    const borderStyle = this.getBorderStyleClass(style);
    const borderRadius = this.getBorderRadiusClass(radius);
    // Only combine non-empty classes
    const borderClass = [borderColor, borderWidth, borderStyle, borderRadius].filter(Boolean).join(' ').trim();
    return ` ${borderClass} `;
  }

  private areAllBorderPropsUndefined(
    color?: string,
    width?: GsStructBorderWidth,
    style?: GsStructBorderStyle,
    radius?: GsStructBorderRadius
  ): boolean {
    return (
      (color === null || color === undefined) &&
      (width === null || width === undefined) &&
      (style === null || style === undefined) &&
      (radius === null || radius === undefined)
    );
  }

  private getBorderColorClass(color?: Color): string {
    return color ? `border-${color}` : '';
  }

  private getBorderWidthClass(width?: GsStructBorderWidth): string {
    if (width === null || width === undefined) return '';
    return width === GsStructBorderWidth.onePixel ? 'border' : `border-${BORDERWIDTH[width]}`;
  }

  private getBorderStyleClass(style?: GsStructBorderStyle): string {
    if (style === null || style === undefined || style === GsStructBorderStyle.none) return '';
    return `border-${BORDERSTYLES[style]}`;
  }

  /**
   * Returns the border radius class, optionally capped at a maximum. The cap is for elements that
   * take the radius of the buttons over: a `full` button is a pill, a `full` card is not.
   * @param radius The configured radius.
   * @param max The largest radius the element accepts, larger values fall back to it.
   * @returns The rounded utility class, empty when no radius is configured.
   */
  public getBorderRadiusClass(radius?: GsStructBorderRadius, max?: GsStructBorderRadius): string {
    if (radius === null || radius === undefined) return '';
    // The enum values ascend by size, so comparing them is enough.
    const capped = max !== undefined && radius > max ? max : radius;
    return capped === GsStructBorderRadius.regular ? 'rounded' : `rounded-${BORDERRADIUS[capped]}`;
  }

  /** Returns the width classes */
  public getWidthClasses(width?: IInputWidth): string {
    if (!width) return '';
    let widthClasses = '';
    const defaultClasses = Object.keys(DEFAULT_WIDTH_CLASSES);
    for (let index = 0; index < defaultClasses.length; index++) {
      const key = defaultClasses[index];
      const defaultWidth = index === 0 ? 12 : width[defaultClasses[index - 1] as keyof IInputWidth];
      const colWidth = Number(width[key as keyof IInputWidth] ?? defaultWidth);
      if (colWidth >= 0 && colWidth <= 12) widthClasses += ` ${DEFAULT_WIDTH_CLASSES[key]}:col-span-${colWidth} `;
    }
    return widthClasses;
  }

  /** Returns the grid column class (used for blocks in page) */
  public getGridColClass(columns: number | ResponsiveAttribute<number>): string {
    if (isResponsiveAttribute(columns)) {
      // Work with responsive attribute number
      const responsiveClasses = this.getResponsiveClasses<number>(columns, (val) => this.getGridColClass(val));
      // A configuration without a `default` gives the block no base span, so keep full width below
      // the lowest configured breakpoint.
      return columns.default == null ? ' col-span-12 ' + responsiveClasses : responsiveClasses;
    } else if (typeof columns === 'number') {
      // Work with number
      return ` col-span-${columns} `;
    }
    // Fallback to full width
    return ' col-span-12 ';
  }

  /**
   * Builds the `sizes` attribute of an image, so the browser picks the smallest sufficient srcset
   * variant instead of guessing. An image slot is the viewport times the block's share of the
   * 12-column grid divided by the items per row — both factors matter: a gallery set to one item per
   * row in a half-width block needs half the viewport, not a third of it and not all of it.
   *
   * Queries are emitted largest-first so the browser's first match wins, with the mobile value last;
   * a breakpoint resolving to the same width as the next smaller one is dropped. The vw is rounded up
   * so the browser errs towards the larger candidate rather than a visibly upscaled one.
   * @param blockColumns The surrounding block's column span (out of 12); full width when unset.
   * @param itemsPerRow The component's items-per-row settings; a single item per row when unset.
   * @returns A `sizes` string (e.g. "(min-width: 992px) 17vw, (min-width: 768px) 50vw, 100vw").
   */
  public getImageSizes(
    blockColumns?: MaybeResponsive<number>,
    itemsPerRow?: Partial<Pick<GsStructPageItems, 'itemsRowDesktop' | 'itemsRowTablet' | 'itemsRowMobile'>>
  ): string {
    // Items per row as a responsive attribute, so both factors resolve through the same cascade:
    // `getGridClasses` renders desktop as `lg:`, tablet as `md:` and mobile unprefixed.
    const items: ResponsiveAttribute<number> = {
      default: itemsPerRow?.itemsRowMobile ?? 1,
      md: itemsPerRow?.itemsRowTablet,
      lg: itemsPerRow?.itemsRowDesktop
    };
    const slotVw = (breakpoint: Breakpoint): number => {
      const columns = this.resolveResponsiveValueAt(blockColumns, breakpoint) ?? 12;
      const perRow = this.resolveResponsiveValueAt(items, breakpoint) ?? 1;
      return Math.min(100, Math.max(1, Math.ceil((100 * (columns / 12)) / Math.max(1, perRow))));
    };
    // Only a breakpoint carrying its own setting can change the slot width; the others inherit it.
    const relevant = BREAKPOINTS.filter(
      (bp) => bp === 'default' || (isResponsiveAttribute(blockColumns) && blockColumns[bp] != null) || items[bp] != null
    );
    const segments: string[] = [];
    let smallerVw: number | null = null;
    // Ascending, so each breakpoint can be compared against the one it would inherit from; unshift
    // then yields the largest-first order the browser expects.
    for (const bp of relevant) {
      const vw = slotVw(bp);
      if (vw === smallerVw) continue;
      smallerVw = vw;
      segments.unshift(bp === 'default' ? `${vw}vw` : `(min-width: ${TAILWIND_BREAKPOINTS[bp]}px) ${vw}vw`);
    }
    return segments.join(', ');
  }

  /**
   * Resolves a responsive value at a given breakpoint, mobile-first: the asked-for breakpoint, then
   * the next smaller ones, finally `default`. Mirrors how the rendered breakpoint-prefixed classes
   * cascade, which is why a missing breakpoint must not fall straight back to `default`.
   * @param value A plain value, a responsive attribute, or nothing.
   * @param breakpoint The breakpoint to resolve for.
   * @returns The value that applies at that breakpoint, or undefined.
   */
  private resolveResponsiveValueAt<T>(value: MaybeResponsive<T> | undefined, breakpoint: Breakpoint): T | undefined {
    if (value == null) return undefined;
    if (!isResponsiveAttribute<T>(value)) return typeof value === 'object' ? undefined : (value as T);
    const fallbackOrder = BREAKPOINTS.filter((bp) => TAILWIND_BREAKPOINTS[bp] <= TAILWIND_BREAKPOINTS[breakpoint]).reverse();
    for (const bp of fallbackOrder) {
      if (value[bp] != null) return value[bp];
    }
    return value.default;
  }

  /** Returns the grid classes */
  public getGridClasses(desktop?: number, tablet?: number, mobile?: number): string {
    let gridClasses = '';
    if (desktop) gridClasses += ` lg:grid-cols-${desktop} `;
    if (tablet) gridClasses += ` md:grid-cols-${tablet} `;
    if (mobile) gridClasses += ` grid-cols-${mobile} `;
    return gridClasses;
  }

  /**
   * Returns the grid column classes for an items-per-row setting configured per breakpoint.
   * @param columns The items per row, fixed or per breakpoint. A grid only takes whole columns, so decimals are rounded down.
   * @returns The breakpoint-prefixed `grid-cols-*` classes, empty when nothing is configured.
   */
  public getResponsiveGridClasses(columns?: MaybeResponsive<number>): string {
    const toClass = (value: number): string => ` grid-cols-${Math.min(12, Math.max(1, Math.floor(value)))} `;
    if (isResponsiveAttribute<number>(columns)) return this.getResponsiveClasses<number>(columns, toClass);
    return typeof columns === 'number' ? toClass(columns) : '';
  }

  /** Returns the width classes for the items in a row */
  public getItemRowWidthClasses(desktop?: number, tablet?: number, mobile?: number): string {
    let itemRowWidthClasses = '';
    if (desktop) itemRowWidthClasses += this.desktopItemRowWidthAmount(desktop);
    if (tablet) itemRowWidthClasses += this.tabletItemRowWidthAmount(tablet);
    if (mobile) itemRowWidthClasses += this.mobileItemRowWidthAmount(mobile);
    return itemRowWidthClasses;
  }

  /** Returns the mobile amount of the width classes for the items in a row */
  public mobileItemRowWidthAmount(itemsRowMobile: number): string {
    return (itemsRowMobile ?? 1) > 1 ? ' w-1/' + itemsRowMobile + ' basis-1/' + itemsRowMobile : ' w-full basis-full ';
  }

  /** Returns the tablet amount of the width classes for the items in a row */
  public tabletItemRowWidthAmount(itemsRowTablet: number): string {
    return (itemsRowTablet ?? 1) > 1 ? ' md:w-1/' + itemsRowTablet + ' md:basis-1/' + itemsRowTablet : ' md:w-full sm:basis-full ';
  }

  /** Returns the desktop amount of the width classes for the items in a row */
  public desktopItemRowWidthAmount(itemsRowDesktop: number): string {
    return (itemsRowDesktop ?? 1) > 1 ? ' md:w-1/' + itemsRowDesktop + ' md:basis-1/' + itemsRowDesktop : ' md:w-full md:basis-full ';
  }

  /** Returns the class names for CSS styles */
  public getColorClass(
    color: Color | undefined,
    type: 'bg' | 'fill' | 'text' | 'ring' | 'outline' | 'border',
    prefix: 'hover:' | 'focus:' | 'focus-visible:' | 'disabled:' | 'aria-disabled:' | '' = ''
  ): string {
    if (!color) return '';
    if (isTailwindColor(color)) return ` ${prefix}${type}-${color} `;
    else if (isHexColor(color)) return '';
    return type === 'bg' ? ` ${prefix}${type}-transparent ` : ` ${prefix}${type}-black `;
  }

  /** Returns the style value if it is a hex color */
  public getColorStyle(color: Color | undefined): string {
    if (!color) return '';
    return color?.startsWith('#') ? color : '';
  }

  /** Set button width and font size of thew button */
  public getButtonWidth(buttonSize: GsStructButtonSize = 1): string {
    return buttonSize !== undefined && buttonSize !== null ? BUTTON_SIZE_CLASSES[buttonSize] : BUTTON_SIZE_CLASSES[1];
  }

  /**
   * Whether the current device size is below the given breakpoint.
   * @param until - Breakpoint from which the desktop variant applies (exclusive)
   * @returns true if the current device size is smaller than `until`
   */
  public isMobile(until: Breakpoint = 'lg'): boolean {
    return TAILWIND_BREAKPOINTS[this.currentDeviceSize()] < TAILWIND_BREAKPOINTS[until];
  }

  /**
   * Gets the current responsive value based on current device size
   * Falls back through breakpoints in mobile-first order
   *
   * Not server-side-rendering safe: the device size comes from a `ResizeObserver` the server does not
   * have, so this returns the `default` value during SSR and the client corrects it on hydration. For
   * anything that has to render identically on both, use {@link getResponsiveStyle} (via the
   * `gsResponsive` directive) and let CSS pick the breakpoint.
   * @param value - ResponsiveAttribute<T> or T
   * @param skipUntil - Optional breakpoint to skip values until (inclusive) - returns undefined if current breakpoint is less than skipUntil
   * @param skipFrom - Optional breakpoint to skip values from (inclusive) - returns undefined if current breakpoint is greater than or equal to skipFrom
   * @returns Current value for the active breakpoint or undefined
   */
  public getCurrentResponsiveValue<T>(value?: ResponsiveAttribute<T> | T, skipUntil?: Breakpoint, skipFrom?: Breakpoint): T | undefined {
    if (value == null) return undefined;
    const currentDevice = this.currentDeviceSize();
    const currentBreakpoint = TAILWIND_BREAKPOINTS[currentDevice];
    // If skipUntil is set, check if current device is less than skipUntil - if so, return undefined
    if (skipUntil && currentBreakpoint < TAILWIND_BREAKPOINTS[skipUntil]) return undefined;
    // If skipFrom is set, check if current device is greater than skipFrom - if so, return undefined
    if (skipFrom && currentBreakpoint >= TAILWIND_BREAKPOINTS[skipFrom]) return undefined;
    return this.resolveResponsiveValueAt<T>(value, currentDevice);
  }

  /**
   * Sets the current responsive value for the active device size (keeps type by default)
   * @param value to set
   * @param newValue to set for the current device
   * @param forceResponsive whether to force the result to be a ResponsiveAttribute<T>
   * @returns Updated ResponsiveAttribute<T> or T - depending on forceResponsive and the input-value type
   */
  public setCurrentResponsiveValue<T>(value: T | ResponsiveAttribute<T>, newValue: T, forceResponsive = false): ResponsiveAttribute<T> | T {
    if (!value) return forceResponsive ? { default: newValue } : newValue;
    if (isResponsiveAttribute<T>(value)) {
      const currentDevice = this.currentDeviceSize();
      return { ...value, [currentDevice]: newValue };
    } else {
      return forceResponsive ? { default: newValue } : newValue;
    }
  }

  /**
   * Turns configured responsive values into CSS custom properties, so the breakpoint is picked by CSS
   * instead of by `getCurrentResponsiveValue()`. Use this wherever a configurable value has to survive
   * server-side rendering: the JS path depends on `window` and a `ResizeObserver`, which the server
   * does not have, so it would render the mobile variant for every visitor and jump on hydration.
   *
   * The mobile-first cascade is resolved here, and the value is emitted for every breakpoint, so an
   * element never inherits a breakpoint variable from an ancestor binding the same value. A value that
   * is not configured produces neither a class nor a variable, so the element is left alone.
   *
   * Applied to an element by the `gsResponsive` directive; the element needs a `gs` query container
   * above it (`.gs-responsive-container`), otherwise only the `default` value applies.
   * @param input The responsive values, keyed by the value name used in `[gsResponsive]`.
   * @returns The classes to add and the custom properties to set.
   * @example
   * styleService.getResponsiveStyle({ 'font-size': config.textSize, padding: config.padding });
   */
  public getResponsiveStyle(input: ResponsiveStyleInput | undefined): ResponsiveStyle {
    const style: ResponsiveStyle = { classes: [], variables: {} };
    if (!input) return style;
    for (const [key, value] of Object.entries(input) as [ResponsiveStyleProperty, unknown][]) {
      if (value == null) continue;
      switch (key) {
        case 'padding':
          this.collectResponsiveProperty(style, key, this.sidesResolver(value as MaybeResponsive<ISides<number>>, PADDING_SIDES));
          break;
        case 'padding-inline':
          this.collectResponsiveProperty(style, key, this.sidesResolver(value as MaybeResponsive<ISides<number>>, INLINE_SIDES));
          break;
        case 'visible':
          this.collectResponsiveProperty(style, key, (breakpoint) =>
            this.resolveResponsiveValueAt<boolean>(value as MaybeResponsive<boolean>, breakpoint) ? 'contents' : 'none'
          );
          break;
        case 'display':
        case 'align-items':
          // Keyword values reach CSS unchanged; only the cascade is resolved here.
          this.collectResponsiveProperty(style, key, (breakpoint) =>
            this.resolveResponsiveValueAt<string>(value as MaybeResponsive<string>, breakpoint)
          );
          break;
        default:
          this.collectResponsiveProperty(style, key, (breakpoint) => {
            const resolved = this.resolveResponsiveValueAt<number | string>(value as MaybeResponsive<number | string>, breakpoint);
            if (typeof resolved === 'string') return resolved;
            return typeof resolved === 'number' && Number.isFinite(resolved) ? `${resolved}px` : undefined;
          });
      }
    }
    return style;
  }

  /**
   * Builds the per-breakpoint resolver for a spacing value. The sides cascade individually — a
   * breakpoint that configures no value for one side inherits it from the breakpoint below instead of
   * collapsing to `0`, which is what `getPadding()` does when it cascades on the whole object.
   * @param sides The configured spacing, responsive or fixed.
   * @param order The sides to emit, in the order the CSS shorthand expects them.
   * @returns A function returning the shorthand for a breakpoint, or undefined where nothing applies.
   */
  private sidesResolver(
    sides: MaybeResponsive<ISides<number>>,
    order: readonly (keyof ISides<number>)[]
  ): (breakpoint: Breakpoint) => string | undefined {
    const perSide = order.map((side) => this.projectSide(sides, side));
    return (breakpoint) => {
      const values = perSide.map((side) => {
        const value = this.resolveResponsiveValueAt<number>(side, breakpoint);
        return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
      });
      if (values.every((value) => value === undefined)) return undefined;
      return values.map((value) => `${value ?? 0}px`).join(' ');
    };
  }

  /**
   * Projects one side out of a responsive `ISides`, keeping the per-breakpoint structure so the
   * cascade still applies to it.
   * @param sides The configured spacing, responsive or fixed.
   * @param side The side to project.
   * @returns The side's value per breakpoint, or the plain value for a non-responsive spacing.
   */
  private projectSide(sides: MaybeResponsive<ISides<number>>, side: keyof ISides<number>): MaybeResponsive<number> | undefined {
    if (!isResponsiveAttribute<ISides<number>>(sides)) return sides[side];
    const perBreakpoint: ResponsiveAttribute<number> = { default: sides.default?.[side] };
    for (const breakpoint of BREAKPOINTS) {
      const value = sides[breakpoint]?.[side];
      if (value != null) perBreakpoint[breakpoint] = value;
    }
    return perBreakpoint;
  }

  /**
   * Emits the custom properties of a single value for every breakpoint, and adds the class that reads
   * them. Breakpoints below the first configured value get the neutral value.
   *
   * Every breakpoint is emitted on purpose, even where it repeats the one below: custom properties are
   * inherited, so a variable left out here would be taken from an ancestor binding the same value —
   * e.g. the spacer line would pick up the spacer's own height from `lg` on (CS4-2693).
   * @param style The style being built, extended in place.
   * @param key The value name, which is also the class and variable name.
   * @param resolve Returns the CSS value at a breakpoint, or undefined where nothing applies.
   */
  private collectResponsiveProperty(
    style: ResponsiveStyle,
    key: ResponsiveStyleProperty,
    resolve: (breakpoint: Breakpoint) => string | undefined
  ): void {
    const cssValues = BREAKPOINTS.map((breakpoint) => resolve(breakpoint));
    // Nothing configured means no class either — an unbound style leaves the element alone.
    if (cssValues.every((cssValue) => cssValue === undefined)) return;
    let current: string = RESPONSIVE_STYLE_PROPERTIES[key].neutral;
    BREAKPOINTS.forEach((breakpoint, index) => {
      current = cssValues[index] ?? current;
      style.variables[`--gs-r-${key}-${breakpoint}`] = current;
    });
    style.classes.push(`gs-r-${key}`);
  }
}
